/**
 * SpacesHome - Integration in One Editorial Workspace
 *
 * Migrated 2026-05-16 to the Integration-in-One design system.
 * Pattern: editorial page-head + 3-column card grid with hover edit-icon +
 * Create/Edit/Join modals.
 *
 * Tokens scoped via .iio wrapper (App.jsx mounts it on the app shell).
 */
import { useState, useEffect, useMemo } from "react";
import { useTranslation, Trans } from "react-i18next";
import { api } from "../utils/helpers";
import { useToast, ToastContainer } from "../components/ui";
import { useApiQuery } from "../hooks/useApiQuery";

// --- Icon set ------------------------------------------------------------
const SPACE_ICONS = {
  layers:   <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>,
  server:   <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="8" rx="2"/><rect x="2" y="14" width="20" height="8" rx="2"/><circle cx="6" cy="6" r="1"/><circle cx="6" cy="18" r="1"/></svg>,
  database: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/><path d="M3 12c0 1.66 4 3 9 3s9-1.34 9-3"/></svg>,
  cloud:    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M18 10h-1.26A8 8 0 109 20h9a5 5 0 000-10z"/></svg>,
  code:     <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>,
  shield:   <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
  bolt:     <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>,
  globe:    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10A15.3 15.3 0 0112 2z"/></svg>,
};
const ICON_KEYS = ["layers", "server", "database", "cloud", "code", "shield", "bolt", "globe"];

// --- Inline SVG icons used in the UI ------------------------------------
const PencilIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);
const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
);
const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
);

const SpacesHome = ({ spaces, cicdStatus, onRefresh, onOpenSpace }) => {
  const { t, i18n } = useTranslation(["spaces", "gemeinsam"]);
  const { toasts, addToast } = useToast();

  // --- Per-space data ---------------------------------------------------
  const [spInstances, setSpInstances] = useState({});
  const [spHealth, setSpHealth] = useState({});
  const [spInstStats, setSpInstStats] = useState({});
  // Aufgeklappte Instanz-Listen (Spaces mit >3 Instanzen)
  const [spExpanded, setSpExpanded] = useState({});
  // Zahlen folgen der gewaehlten Sprache, nicht fest dem Deutschen: "18.859"
  // heisst auf Englisch achtzehn Komma acht, nicht achtzehntausend. Ein fest
  // eingestelltes Gebietsschema verfaelscht damit die Zahl selbst.
  const gebiet = i18n.language === "en" ? "en-GB" : "de-DE";
  const fmtNum = (x) => (x ?? 0).toLocaleString(gebiet);
  const fmtCompact = (x) => x >= 1e6
    ? t("karte.millionen", { wert: (x / 1e6).toLocaleString(gebiet, { maximumFractionDigits: 1 }) })
    : fmtNum(x);

  const spaceIds = useMemo(() => spaces.map(s => s.id).sort().join(","), [spaces]);
  const { isOptimized } = useApiQuery(
    ["spaces-health", spaceIds],
    "/api/spaces",
    { staleTime: 60000, refetchInterval: 120000, enabled: spaces.length > 0 }
  );

  useEffect(() => {
    if (isOptimized) return;
    spaces.forEach(async s => {
      try {
        const insts = await api(`/api/spaces/${s.id}/instances`);
        setSpInstances(p => ({ ...p, [s.id]: insts }));
        let failed = 0;
        for (const inst of insts) {
          try {
            const st = await api(`/api/instances/${inst.id}/stats`);
            const f = st?.failed || 0;
            failed += f;
            setSpInstStats(p => ({ ...p, [inst.id]: { failed: f, completed: st?.completed || 0, total: st?.total || 0, ageMs: st?._snapshotAgeMs ?? null } }));
          } catch {}
        }
        setSpHealth(p => ({ ...p, [s.id]: failed }));
      } catch {}
    });
  }, [spaces, isOptimized]);

  useEffect(() => {
    if (!isOptimized) return;
    let cancelled = false;
    (async () => {
      await Promise.all(spaces.map(async s => {
        try {
          const insts = await api(`/api/spaces/${s.id}/instances`);
          if (cancelled) return;
          setSpInstances(p => ({ ...p, [s.id]: insts }));
          let failed = 0;
          await Promise.all(insts.map(async inst => {
            try {
              const st = await api(`/api/instances/${inst.id}/stats`);
              const f = st?.failed || 0;
              failed += f;
              if (!cancelled) setSpInstStats(p => ({ ...p, [inst.id]: { failed: f, completed: st?.completed || 0, total: st?.total || 0, ageMs: st?._snapshotAgeMs ?? null } }));
            } catch {}
          }));
          if (!cancelled) setSpHealth(p => ({ ...p, [s.id]: failed }));
        } catch {}
      }));
    })();
    return () => { cancelled = true; };
  }, [spaces, isOptimized]);

  // --- Modal state ------------------------------------------------------
  const [creating, setCreating] = useState(false);
  const [createName, setCreateName] = useState("");
  const [createDesc, setCreateDesc] = useState("");
  const [createIcon, setCreateIcon] = useState("layers");
  // Space aus Vorlage: uebernimmt die Instanzen eines bestehenden Space. Deren Verbindung
  // bleibt Eigentum der Vorlage, damit der Service Key nur an einer Stelle gepflegt wird.
  const [createVorlage, setCreateVorlage] = useState("");
  const [vorlageInstanzen, setVorlageInstanzen] = useState(null); // null = noch nicht geladen
  const [vorlageAuswahl, setVorlageAuswahl] = useState({});

  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [editIcon, setEditIcon] = useState("layers");
  const [editUsers, setEditUsers] = useState([]);
  const [delConfirm, setDelConfirm] = useState("");
  const [inviteLink, setInviteLink] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);

  const [joining, setJoining] = useState(false);
  const [joinCode, setJoinCode] = useState("");

  // ESC closes any modal
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (creating) closeCreate();
      else if (editing) closeEdit();
      else if (joining) closeJoin();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  });

  // --- Handlers ---------------------------------------------------------
  const openCreate = () => {
    setCreateName(""); setCreateDesc(""); setCreateIcon("layers");
    setCreateVorlage(""); setVorlageInstanzen(null); setVorlageAuswahl({});
    setCreating(true);
  };
  const closeCreate = () => setCreating(false);
  // Instanzen der gewaehlten Vorlage holen, damit der Nutzer sieht, was uebernommen wird,
  // und einzelne abwaehlen kann.
  const waehleVorlage = async (id) => {
    setCreateVorlage(id);
    setVorlageInstanzen(null); setVorlageAuswahl({});
    if (!id) return;
    try {
      const list = await api(`/api/spaces/${id}/instances`);
      const arr = Array.isArray(list) ? list : [];
      setVorlageInstanzen(arr);
      setVorlageAuswahl(Object.fromEntries(arr.map(i => [i.id, true])));
    } catch (e) {
      setVorlageInstanzen([]);
      addToast(e.message || t("anlegen.vorlageFehler"), "error");
    }
  };
  const submitCreate = async () => {
    if (!createName.trim()) return;
    try {
      await api("/api/spaces", {
        method: "POST",
        body: JSON.stringify({
          name: createName.trim(), description: createDesc.trim() || undefined, icon: createIcon,
          ...(createVorlage ? {
            vorlageVon: createVorlage,
            instanzen: Object.entries(vorlageAuswahl).filter(([, an]) => an).map(([id]) => id),
          } : {}),
        }),
      });
      {
        const anzahl = Object.values(vorlageAuswahl).filter(Boolean).length;
        addToast(createVorlage
          ? t("anlegen.erfolgVorlage", { count: anzahl })
          : t("anlegen.erfolg"), "success");
      }
      closeCreate();
      onRefresh();
    } catch (e) {
      addToast(e.message, "error");
    }
  };

  const openEdit = async (s) => {
    setEditing(s);
    setEditName(s.name || "");
    setEditDesc(s.description || "");
    setEditIcon(s.icon || "layers");
    setDelConfirm("");
    setInviteLink(null);
    setShareCopied(false);
    try {
      const u = await api(`/api/spaces/${s.id}/users`).catch(() => []);
      setEditUsers(Array.isArray(u) ? u : []);
    } catch { setEditUsers([]); }
  };
  const closeEdit = () => { setEditing(null); setDelConfirm(""); };
  const submitEdit = async () => {
    if (!editing) return;
    const payload = {};
    if (editName.trim() && editName.trim() !== editing.name) payload.name = editName.trim();
    if ((editDesc || "") !== (editing.description || "")) payload.description = editDesc;
    if (editIcon !== editing.icon) payload.icon = editIcon;
    if (Object.keys(payload).length === 0) { closeEdit(); return; }
    try {
      await api(`/api/spaces/${editing.id}`, { method: "PUT", body: JSON.stringify(payload) });
      addToast(t("bearbeiten.gespeichert"), "success");
      closeEdit();
      onRefresh();
    } catch (e) {
      addToast(e.message, "error");
    }
  };
  const submitDelete = async () => {
    if (!editing || delConfirm !== editing.name) return;
    try {
      await api(`/api/spaces/${editing.id}`, { method: "DELETE" });
      addToast(t("bearbeiten.geloescht"), "success");
      closeEdit();
      onRefresh();
    } catch (e) {
      addToast(e.message, "error");
    }
  };
  const createInvite = async () => {
    if (!editing) return;
    setInviteLoading(true); setInviteLink(null);
    try {
      const insts = spInstances[editing.id] || [];
      const r = await api(`/api/spaces/${editing.id}/invite`, {
        method: "POST",
        body: JSON.stringify({ instanceIds: insts.map(i => i.id), baseUrl: window.location.origin }),
      });
      setInviteLink(r.link);
      addToast(t("bearbeiten.einladungErstellt"), "success");
    } catch (e) {
      addToast(e.message, "error");
    }
    setInviteLoading(false);
  };
  const copyInvite = () => {
    if (!inviteLink) return;
    navigator.clipboard.writeText(inviteLink);
    setShareCopied(true);
    setTimeout(() => setShareCopied(false), 1800);
  };

  const openJoin = () => { setJoinCode(""); setJoining(true); };
  const closeJoin = () => setJoining(false);
  const submitJoin = () => {
    if (!joinCode.trim()) return;
    // Real join handled by /invite/:token route - here we redirect to it.
    const code = joinCode.trim();
    const url = code.startsWith("http") ? code : `${window.location.origin}/invite/${code}`;
    window.location.href = url;
  };

  // --- Render -----------------------------------------------------------
  return (
    <>
      <ToastContainer toasts={toasts}/>

      {/* Page head */}
      <div className="iio-page-head">
        <div className="iio-page-head-left iio-anim-arrival">
          <div className="iio-breadcrumb">
            <span className="iio-accent">{t("kopf.brotkrume")}</span>
            <span className="iio-sep">/</span>
            <span>{t("kopf.brotkrumeAktuell")}</span>
          </div>
          <h1>{t("kopf.titel")}</h1>
          <p className="iio-lead">
            {t("kopf.einleitung")}
          </p>
        </div>
        <div className="iio-page-head-right iio-anim-arrival" style={{ animationDelay: "80ms" }}>
          <button className="iio-btn iio-btn--secondary" onClick={openJoin}>{t("gemeinsam:aktion.beitreten")}</button>
          <button className="iio-btn iio-btn--signal" onClick={openCreate}>
            <PlusIcon/>{t("kopf.neuerSpace")}
          </button>
        </div>
      </div>

      {/* Grid */}
      <div className="iio-grid">
        {spaces.map((s, cardIdx) => {
          const insts = spInstances[s.id] || [];
          const statOf = (inst) => spInstStats[inst.id] || { failed: 0, completed: 0, total: 0, ageMs: null };
          const sumTotal = insts.reduce((a, i) => a + statOf(i).total, 0);
          const sumFailed = insts.reduce((a, i) => a + statOf(i).failed, 0);
          // Relevanteste zuerst: Fehler, dann Volumen. Max 3 sichtbar, Rest klappbar.
          const sorted = [...insts].sort((a, b) => (statOf(b).failed - statOf(a).failed) || (statOf(b).total - statOf(a).total));
          const visible = sorted.slice(0, 3);
          const hidden = sorted.slice(3);
          const isOpen = !!spExpanded[s.id];
          const ages = insts.map(i => statOf(i).ageMs).filter(a => a != null);
          const ageMin = ages.length ? Math.round(Math.min(...ages) / 60000) : null;
          const instRow = (inst) => {
            const st = statOf(inst);
            const idle = st.total === 0;
            return (
              <button
                key={inst.id}
                type="button"
                className="iio-sp2-inst"
                title={inst.name}
                onClick={(e) => { e.stopPropagation(); onOpenSpace(s, inst); }}
              >
                <span className={"iio-sp2-led" + (idle ? " idle" : " ok live")}/>
                <span className="iio-sp2-iname">{inst.name}</span>
                <span className="iio-sp2-num"><span className="v">{idle ? "-" : fmtCompact(st.total)}</span><span className="l">{t("karte.verarbeitet")}</span></span>
                <span className="iio-sp2-num"><span className="v">{idle ? "-" : fmtNum(st.failed)}</span><span className="l">{t("karte.fehler24h")}</span></span>
                <svg className="iio-sp2-go" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
              </button>
            );
          };
          return (
            <article
              key={s.id}
              className="iio-space-card iio-sp2-card iio-anim-stagger"
              style={{ animationDelay: `${120 + Math.min(cardIdx, 8) * 40}ms` }}
              tabIndex={0}
              onClick={() => onOpenSpace(s)}
              onKeyDown={(e) => { if (e.key === "Enter") onOpenSpace(s); }}
            >
              {!s.isShared && (
                <button
                  className="iio-card-edit"
                  title={t("karte.bearbeitenTitel")}
                  onClick={(e) => { e.stopPropagation(); openEdit(s); }}
                >
                  <PencilIcon/>
                </button>
              )}

              <div className="iio-card-head">
                <div className="iio-card-icon">{SPACE_ICONS[s.icon] || SPACE_ICONS.layers}</div>
                <div className="iio-card-title">
                  <h3>{s.name}</h3>
                  <div className="iio-meta">
                    {t("gemeinsam:einheit.instanz", { count: insts.length })}
                  </div>
                  {(() => {
                    const c = cicdStatus?.spaces?.[s.id];
                    if (!c || (!c.pipelineReady && !c.configured && !(c.geteiltMit > 0))) return null;
                    // Ecklaschen statt Pills unter dem Titel: der CI/CD-Zustand haengt wie
                    // die Stufen-Lasche an der Kartenecke. Der Status steht aussen und
                    // traegt die Eckrundung, eine Warnung rueckt links daneben.
                    return (
                      <div className="iio-card-ecklaschen">
                        {c.geteiltMit > 0 && (
                          <span className="iio-card-ecklasche iio-card-ecklasche--warn"
                            title={t("karte.repoGeteiltTitel", { count: c.geteiltMit + 1 })}>
                            {t("karte.repoGeteilt")}
                          </span>
                        )}
                        {c.pipelineReady ? (
                          <span className="iio-card-ecklasche" title={t("karte.cicdBereitTitel")}>
                            {t("karte.cicdBereit")}
                          </span>
                        ) : c.configured ? (
                          <span className="iio-card-ecklasche iio-card-ecklasche--warn" title={t("karte.cicdOhneGithubTitel")}>
                            {t("karte.cicdOhneGithub")}
                          </span>
                        ) : null}
                      </div>
                    );
                  })()}
                </div>
                {/* Kein "live"-Status mehr: er war der Normalzustand und trug keine
                    Information. Leer und Inaktiv bleiben - das sind echte Hinweise. */}
                {insts.length === 0
                  ? <span className="iio-status iio-status--idle iio-sp2-status">{t("karte.statusLeer")}</span>
                  : sumTotal === 0
                    ? <span className="iio-status iio-status--idle iio-sp2-status">{t("karte.statusInaktiv")}</span>
                    : null}
              </div>

              <div className={`iio-card-desc${!s.description ? " iio-card-desc--empty" : ""}`}>
                {s.description || t("karte.keineBeschreibung")}
              </div>

              {/* Instanzen direkt auf der Karte: 3 relevanteste sichtbar, Rest klappbar */}
              {insts.length > 0 && (
                <div className={"iio-sp2-insts" + (isOpen ? " open" : "")}>
                  {visible.map(instRow)}
                  {hidden.length > 0 && <>
                    <div className="iio-sp2-more"><div><div className={hidden.length > 5 ? "iio-sp2-scrollwrap" : ""}>
                      {hidden.map(instRow)}
                    </div></div></div>
                    <button
                      type="button"
                      className="iio-sp2-expander"
                      onClick={(e) => { e.stopPropagation(); setSpExpanded(p => ({ ...p, [s.id]: !isOpen })); }}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
                      <span>{isOpen ? t("karte.wenigerAnzeigen") : t("karte.weitereInstanzen", { count: hidden.length })}</span>
                    </button>
                  </>}
                </div>
              )}

              <div className="iio-card-foot iio-sp2-foot">
                <div className="iio-sp2-kpi">
                  <span className="v">{insts.length}</span>
                  <span className="l">{t("karte.tenants")}</span>
                </div>
                <div className="iio-sp2-kpi">
                  <span className="v">{fmtCompact(sumTotal)}</span>
                  <span className="l">{t("karte.verarbeitet24h")}</span>
                </div>
                <div className="iio-sp2-kpi">
                  <span className="v">{fmtNum(sumFailed)}</span>
                  <span className="l">{t("karte.fehler24h")}</span>
                </div>
              </div>
              {ageMin != null && <div className="iio-sp2-sync">{t("gemeinsam:zeit.stand", { zeit: ageMin <= 1 ? t("gemeinsam:zeit.geradeEben") : t("gemeinsam:zeit.vorMinuten", { count: ageMin }) })}</div>}
            </article>
          );
        })}

        {/* New-space dashed card */}
        <article
          className="iio-space-card iio-space-card--new iio-anim-stagger"
          style={{ animationDelay: `${160 + Math.min(spaces.length, 8) * 40}ms` }}
          tabIndex={0}
          onClick={openCreate}
          onKeyDown={(e) => { if (e.key === "Enter") openCreate(); }}
        >
          <div className="iio-plus">+</div>
          <div className="iio-lbl">{t("karte.neuTitel")}</div>
          <div className="iio-sub">{t("karte.neuSub")}</div>
        </article>
      </div>

      {/* =====================  CREATE MODAL  ===================== */}
      {creating && (
        <div className="iio-modal-overlay is-open" onClick={(e) => { if (e.target === e.currentTarget) closeCreate(); }}>
          <div className="iio-modal" role="dialog" aria-modal="true">
            <header className="iio-modal-head">
              <div className="iio-modal-head-left">
                <span className="iio-modal-eyebrow">{t("gemeinsam:aktion.anlegen")}</span>
                <h2>{t("anlegen.titel")}</h2>
              </div>
              <button className="iio-modal-close" onClick={closeCreate}><CloseIcon/></button>
            </header>

            <div className="iio-modal-body">
              <div className="iio-field">
                <label className="iio-field-label" htmlFor="iio-create-name">{t("feld.name")}</label>
                <input id="iio-create-name" className="iio-input" autoFocus value={createName} onChange={(e) => setCreateName(e.target.value)} placeholder={t("anlegen.namePlatzhalter")}/>
                <div className="iio-field-hint">{t("anlegen.nameHinweis")}</div>
              </div>

              <div className="iio-field">
                <label className="iio-field-label" htmlFor="iio-create-desc">{t("feld.beschreibung")}</label>
                <textarea id="iio-create-desc" className="iio-textarea" value={createDesc} onChange={(e) => setCreateDesc(e.target.value)} placeholder={t("feld.beschreibungPlatzhalter")}/>
                <div className="iio-field-hint">{t("anlegen.beschreibungHinweis")}</div>
              </div>

              {/* Aus Vorlage: nur anbieten, wenn es einen Space mit Instanzen gibt. Ein leeres
                  Auswahlfeld wäre ein Versprechen ohne Inhalt. */}
              {spaces.filter(sp => sp.instanceCount > 0 && !sp.isShared && !sp.mirrorOf).length > 0 && (
                <div className="iio-field">
                  <label className="iio-field-label" htmlFor="iio-create-vorlage">{t("anlegen.vorlage")}</label>
                  <select id="iio-create-vorlage" className="iio-input" value={createVorlage} onChange={e => waehleVorlage(e.target.value)}>
                    <option value="">{t("anlegen.vorlageOhne")}</option>
                    {/* Nur eigene Spaces: das Backend weist geteilte als Vorlage mit 403 ab, ein Angebot
                        waere hier also ein Weg, der nicht funktioniert. */}
                    {spaces.filter(sp => sp.instanceCount > 0 && !sp.isShared && !sp.mirrorOf).map(sp => (
                      <option key={sp.id} value={sp.id}>{t("anlegen.vorlageOption", { name: sp.name, count: sp.instanceCount })}</option>
                    ))}
                  </select>
                  <div className="iio-field-hint">
                    {createVorlage
                      ? t("anlegen.vorlageHinweisGewaehlt")
                      : t("anlegen.vorlageHinweisOhne")}
                  </div>
                </div>
              )}

              {/* Vorbelegt sind alle Instanzen, weil das der häufigere Fall ist. Abwählen bleibt möglich. */}
              {createVorlage && (
                <div className="iio-field" role="group" aria-labelledby="iio-vorlage-auswahl-lbl">
                  <span className="iio-field-label" id="iio-vorlage-auswahl-lbl">{t("anlegen.auswahlLabel")}</span>
                  {vorlageInstanzen === null ? (
                    <div className="iio-field-hint">{t("anlegen.auswahlLaedt")}</div>
                  ) : vorlageInstanzen.length === 0 ? (
                    <div className="iio-field-hint" style={{ marginTop: 4, padding: "8px 10px", borderLeft: "3px solid var(--iio-warning)", background: "color-mix(in srgb, var(--iio-warning) 6%, var(--iio-bg-1))", borderRadius: "var(--iio-r-sm)", color: "var(--iio-text-2)" }}>{t("anlegen.auswahlLeer")}</div>
                  ) : (
                    <div style={{ display: "grid", gap: 6 }}>
                      {vorlageInstanzen.map(i => (
                        <label key={i.id} style={{ display: "grid", gridTemplateColumns: "auto minmax(0,1fr)", gap: 8, alignItems: "baseline", fontSize: 13, cursor: "pointer" }}>
                          <input type="checkbox" checked={!!vorlageAuswahl[i.id]} style={{ accentColor: "var(--iio-brand-ink)", width: 14, height: 14 }}
                            onChange={e => setVorlageAuswahl(p => ({ ...p, [i.id]: e.target.checked }))}/>
                          <span style={{ minWidth: 0, overflowWrap: "anywhere" }}>
                            <b style={{ color: "var(--iio-text-1)" }}>{i.name}</b>
                            {i.stage && <span style={{ color: "var(--iio-text-3)" }}> · {i.stage.toUpperCase()}</span>}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="iio-field">
                <span className="iio-field-label">{t("feld.symbol")}</span>
                <div className="iio-icon-picker">
                  {ICON_KEYS.map(key => (
                    <button key={key} type="button" className={`iio-icon-pick${createIcon === key ? " is-active" : ""}`} title={t(`symbol.${key}`)} onClick={() => setCreateIcon(key)}>
                      {SPACE_ICONS[key]}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <footer className="iio-modal-foot">
              <div className="iio-modal-foot-left"/>
              <div className="iio-modal-foot-right">
                <button className="iio-btn iio-btn--secondary" onClick={closeCreate}>{t("gemeinsam:aktion.abbrechen")}</button>
                {/* Die Beschriftung nennt den Unterschied: aus einer Vorlage entstehen auch Instanzen. */}
                <button className="iio-btn iio-btn--signal" onClick={submitCreate}
                  disabled={!createName.trim() || (!!createVorlage && !Object.values(vorlageAuswahl).some(Boolean))}>
                  {createVorlage ? t("anlegen.knopfVorlage") : t("anlegen.knopf")}
                </button>
              </div>
            </footer>
          </div>
        </div>
      )}

      {/* =====================  EDIT MODAL  ===================== */}
      {editing && (
        <div className="iio-modal-overlay is-open" onClick={(e) => { if (e.target === e.currentTarget) closeEdit(); }}>
          <div className="iio-modal" role="dialog" aria-modal="true">
            <header className="iio-modal-head">
              <div className="iio-modal-head-left">
                <span className="iio-modal-eyebrow">{t("gemeinsam:aktion.bearbeiten")}</span>
                <h2>{t("bearbeiten.titel")}</h2>
              </div>
              <button className="iio-modal-close" onClick={closeEdit}><CloseIcon/></button>
            </header>

            <div className="iio-modal-body">
              <div className="iio-field">
                <label className="iio-field-label" htmlFor="iio-edit-name">{t("feld.name")}</label>
                <input id="iio-edit-name" className="iio-input" value={editName} onChange={(e) => setEditName(e.target.value)}/>
              </div>

              <div className="iio-field">
                <label className="iio-field-label" htmlFor="iio-edit-desc">{t("feld.beschreibung")}</label>
                <textarea id="iio-edit-desc" className="iio-textarea" value={editDesc} onChange={(e) => setEditDesc(e.target.value)} placeholder={t("feld.beschreibungPlatzhalter")}/>
                <div className="iio-field-hint">{t("bearbeiten.beschreibungHinweis")}</div>
              </div>

              <div className="iio-field">
                <span className="iio-field-label">{t("feld.symbol")}</span>
                <div className="iio-icon-picker">
                  {ICON_KEYS.map(key => (
                    <button key={key} type="button" className={`iio-icon-pick${editIcon === key ? " is-active" : ""}`} title={t(`symbol.${key}`)} onClick={() => setEditIcon(key)}>
                      {SPACE_ICONS[key]}
                    </button>
                  ))}
                </div>
              </div>

              {/* Share / invite */}
              {!editing.isShared && (
                <div className="iio-field">
                  <span className="iio-field-label">{t("bearbeiten.einladung")}</span>
                  <div className="iio-join-info">
                    {t("bearbeiten.einladungInfo")}
                  </div>
                  {!inviteLink ? (
                    <button
                      className="iio-btn iio-btn--secondary"
                      style={{ marginTop: 8, alignSelf: "flex-start" }}
                      onClick={createInvite}
                      disabled={inviteLoading || (spInstances[editing.id] || []).length === 0}
                    >
                      {inviteLoading ? t("bearbeiten.einladungLaeuft") : t("bearbeiten.einladungErstellen")}
                    </button>
                  ) : (
                    <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
                      <input className="iio-input" readOnly value={inviteLink} style={{ fontFamily: "var(--iio-font-mono)", fontSize: 12 }}/>
                      <button className="iio-btn iio-btn--secondary" onClick={copyInvite}>
                        {shareCopied ? t("bearbeiten.kopiert") : t("bearbeiten.kopieren")}
                      </button>
                    </div>
                  )}
                  {(spInstances[editing.id] || []).length === 0 && (
                    <div className="iio-field-hint">{t("bearbeiten.einladungBrauchtInstanzen")}</div>
                  )}
                </div>
              )}

              {/* Members */}
              {editUsers.length > 0 && (
                <div className="iio-field">
                  <span className="iio-field-label">{t("bearbeiten.zugriff", { count: editUsers.length })}</span>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {editUsers.map((u, i) => (
                      <div key={i} style={{
                        display: "flex", alignItems: "center", gap: 10,
                        padding: "8px 12px",
                        border: "1px solid var(--iio-border-subtle)",
                        borderRadius: "var(--iio-r-sm)",
                        background: "var(--iio-bg-1)",
                        fontSize: 13,
                      }}>
                        <span style={{
                          width: 24, height: 24, borderRadius: "var(--iio-r-sm)",
                          background: "var(--iio-brand-ink)", color: "var(--iio-hero-text)",
                          display: "inline-flex", alignItems: "center", justifyContent: "center",
                          fontSize: 10, fontWeight: 600,
                        }}>
                          {(u.displayName || u.username || "?")[0]?.toUpperCase()}
                        </span>
                        <span style={{ flex: 1 }}>{u.displayName || u.username}</span>
                        {u.email && <span style={{ fontFamily: "var(--iio-font-mono)", fontSize: 11, color: "var(--iio-text-3)" }}>{u.email}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Delete confirmation */}
              {!editing.isShared && (
                <div className="iio-field">
                  <span className="iio-field-label" style={{ color: "var(--iio-critical)" }}>{t("loeschen.titel")}</span>
                  <div style={{
                    padding: 12,
                    border: "1px solid var(--iio-critical)",
                    borderLeft: "3px solid var(--iio-critical)",
                    borderRadius: "var(--iio-r-sm)",
                    background: "var(--iio-bg-1)",
                    display: "flex", flexDirection: "column", gap: 10,
                  }}>
                    <div style={{ fontSize: 13, color: "var(--iio-text-1)" }}>
                      <Trans t={t} i18nKey="loeschen.hinweis" values={{ name: editing.name }}/>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <input className="iio-input" value={delConfirm} onChange={(e) => setDelConfirm(e.target.value)} placeholder={editing.name}/>
                      <button className="iio-btn iio-btn--danger" disabled={delConfirm !== editing.name} onClick={submitDelete}>{t("gemeinsam:aktion.loeschen")}</button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <footer className="iio-modal-foot">
              <div className="iio-modal-foot-left"/>
              <div className="iio-modal-foot-right">
                <button className="iio-btn iio-btn--secondary" onClick={closeEdit}>{t("gemeinsam:aktion.abbrechen")}</button>
                <button className="iio-btn iio-btn--signal" onClick={submitEdit}>{t("bearbeiten.speichern")}</button>
              </div>
            </footer>
          </div>
        </div>
      )}

      {/* =====================  JOIN MODAL  ===================== */}
      {joining && (
        <div className="iio-modal-overlay is-open" onClick={(e) => { if (e.target === e.currentTarget) closeJoin(); }}>
          <div className="iio-modal" role="dialog" aria-modal="true">
            <header className="iio-modal-head">
              <div className="iio-modal-head-left">
                <span className="iio-modal-eyebrow">{t("gemeinsam:aktion.beitreten")}</span>
                <h2>{t("beitreten.titel")}</h2>
              </div>
              <button className="iio-modal-close" onClick={closeJoin}><CloseIcon/></button>
            </header>

            <div className="iio-modal-body">
              <div className="iio-join-info">
                {t("beitreten.info")}
              </div>
              <div className="iio-field">
                <label className="iio-field-label" htmlFor="iio-join-code">{t("beitreten.codeLabel")}</label>
                <input id="iio-join-code" className="iio-input" autoFocus value={joinCode} onChange={(e) => setJoinCode(e.target.value)} placeholder={t("beitreten.codePlatzhalter")}/>
              </div>
            </div>

            <footer className="iio-modal-foot">
              <div className="iio-modal-foot-left"/>
              <div className="iio-modal-foot-right">
                <button className="iio-btn iio-btn--secondary" onClick={closeJoin}>{t("gemeinsam:aktion.abbrechen")}</button>
                <button className="iio-btn iio-btn--signal" onClick={submitJoin} disabled={!joinCode.trim()}>{t("gemeinsam:aktion.beitreten")}</button>
              </div>
            </footer>
          </div>
        </div>
      )}
    </>
  );
};

export default SpacesHome;
